import { connect, type ConnectedProps } from 'react-redux';
import { type SubmitHandler } from 'react-hook-form';
import { useCallback, useMemo, useRef, useState } from 'react';
import IncidentForm from '@/components/IncidentForm';
import { type FormElement } from '@/components/Form';
import { currentEvent, incidentById, editIncident } from '@/slices/events';
import { type RootState } from '@/store';
import { ModalLayout } from '../../layouts';
import { type ModalContentProps } from '../../../state';

type FormValues = {
  time: string;
  action_id: string;
  title: string;
  content: string;
  highlighted: boolean;
  author_id: string;
  is_media: boolean;
};

type Props = ModalContentProps & ConnectedProps<typeof connector>;

const EditIncidentModal = ({
  closeModal,
  incident,
  event,
  editIncident,
}: Props) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const formRef = useRef<FormElement>(null);

  const defaultValues: FormValues = useMemo(
    () => ({
      time: incident.time || '',
      action_id: String(incident.action_id) || '',
      title: incident.title || '',
      content: incident.content || '',
      highlighted: incident.highlighted || false,
      author_id: incident.author_id || '',
      is_media: incident.is_media || false,
    }),
    [incident],
  );

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    ({ time, action_id, title, content, highlighted, author_id, is_media }) => {
      editIncident({
        id: incident.id,
        time,
        action_id: Number(action_id) || null,
        action_raw: event?.sport?.actions?.find(
          (action) => action.id === Number(action_id),
        )?.name,
        title,
        content,
        highlighted,
        author_id: author_id || null,
        is_media,
      });
      closeModal();
    },
    [closeModal, editIncident, event?.sport?.actions, incident.id],
  );

  return (
    <ModalLayout
      closeModal={closeModal}
      title="Edit Incident"
      onAccept={handleAccept}
      acceptText="Save"
      acceptDisabled={isSubmitDisabled}
      onCancel={closeModal}
      cancelText="Cancel"
    >
      <IncidentForm
        ref={formRef}
        editorId="edit-incident-editor"
        defaultValues={defaultValues}
        onValidationChange={setIsSubmitDisabled}
        onSubmit={onSubmit}
      />
    </ModalLayout>
  );
};

const connector = connect(
  (
    state: RootState,
    { modalData: { incidentId } = {} }: ModalContentProps,
  ) => ({
    incident: incidentById(state, { incidentId }),
    event: currentEvent(state),
  }),
  {
    editIncident,
  },
);

export default connector(EditIncidentModal);
